import React, { useState, useEffect } from 'react';
import { Sparkles, Briefcase, Zap, Package } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  LoadingState,
} from '@/components/ui';
import { getCollectionDetail, type CollectionDetail } from '@/api/onboarding';
import type { CollectionOption } from '../types';

// Map collection IDs to icons
const COLLECTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  fashion: Sparkles,
  business: Briefcase,
  sport: Zap,
};

interface CollectionDetailModalProps {
  collection: CollectionOption | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void;
  isSelected: boolean;
}

/** Small tag for a product name or colour. */
function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-chip bg-paper-3 px-2.5 py-1 text-sm font-medium text-ink">
      {children}
    </span>
  );
}

/**
 * Dialog with the details of a collection. Loads the full record from the
 * API so product names replace the slugs of the summary.
 */
export function CollectionDetailModal({
  collection,
  isOpen,
  onClose,
  onSelect,
  isSelected,
}: CollectionDetailModalProps) {
  const [detail, setDetail] = useState<CollectionDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // Load collection detail when the dialog opens
  const collectionId = collection?.id;
  useEffect(() => {
    if (isOpen && collectionId) {
      setLoading(true);
      getCollectionDetail(collectionId)
        .then(setDetail)
        .catch((err) => {
          console.error('Failed to load collection detail:', err);
          setDetail(null);
        })
        .finally(() => setLoading(false));
    } else {
      setDetail(null);
    }
  }, [isOpen, collectionId]);

  if (!collection) return null;

  // Get icon for collection (default to Package if not found)
  const IconComponent = COLLECTION_ICONS[collection.id] || Package;

  // Use detail data for products if available, otherwise fall back to collection
  const productNames =
    detail?.products.map((p) => p.name) || collection.products;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel="Close" className="max-w-2xl">
        <img
          src={collection.image}
          alt=""
          className="h-40 w-full rounded-card object-cover ring-1 ring-line"
        />
        <div className="flex items-center gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-primary-soft text-primary"
            aria-hidden="true"
          >
            <IconComponent className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <DialogTitle>{collection.title}</DialogTitle>
            <DialogDescription>{collection.description}</DialogDescription>
          </div>
        </div>

        <div className="space-y-5">
          <section>
            <h4 className="dc-eyebrow mb-3">Included Products</h4>
            {loading ? (
              <LoadingState label="Loading products..." className="py-2" />
            ) : (
              <div className="flex flex-wrap gap-2">
                {productNames.map((product) => (
                  <Tag key={product}>{product}</Tag>
                ))}
              </div>
            )}
          </section>

          <section>
            <h4 className="dc-eyebrow mb-3">Available Colors</h4>
            <div className="flex flex-wrap gap-2">
              {collection.colors.map((color) => (
                <Tag key={color}>{color}</Tag>
              ))}
            </div>
          </section>

          <section>
            <h4 className="dc-eyebrow mb-2">Style &amp; Theme</h4>
            <p className="text-sm text-ink">{collection.style}</p>
          </section>
        </div>

        <DialogFooter className="sm:items-center sm:justify-between">
          <p className="text-sm text-mute">
            {isSelected ? 'Currently selected' : 'Not yet selected'}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button variant="primary" onClick={onSelect}>
              {isSelected ? 'Selected' : 'Select'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
